// Public Client Self-Service Intake Page (no auth required)
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Scale, 
  Upload, 
  FileText, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  X,
  User,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Users,
  Gavel,
  Calendar,
  ChevronRight,
  ChevronLeft,
  Plus,
  Trash2,
  File,
  Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Relationship types for related parties
const RELATIONSHIP_TYPES = [
  { id: 'child', name: 'Child' },
  { id: 'spouse', name: 'Spouse' },
  { id: 'parent', name: 'Parent' },
  { id: 'sibling', name: 'Sibling' },
  { id: 'guardian_ad_litem', name: 'Guardian ad Litem' },
  { id: 'family_member', name: 'Other Family Member' },
  { id: 'witness', name: 'Witness' },
  { id: 'expert', name: 'Expert' },
  { id: 'other', name: 'Other' }
];

export default function ClientIntakePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [intakeInfo, setIntakeInfo] = useState(null);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [confirmation, setConfirmation] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    // Contact Info
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    preferred_contact_method: 'phone',
    
    // Case Info
    case_type: '',
    case_description: '',
    lead_source: 'website',
    
    // Related Parties
    opposing_parties: [],
    other_related_parties: [],
    
    // Attorneys
    prior_attorneys: [],
    opposing_attorneys: [],
    
    // Court Dates
    known_court_dates: [],
    
    // Notes
    additional_notes: ''
  });
  
  // File state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchIntakeInfo();
  }, [token]);

  const fetchIntakeInfo = async () => {
    try {
      const res = await fetch(`${API_URL}/api/intake/client-intake/${token}`);
      if (res.ok) {
        setIntakeInfo(await res.json());
      } else {
        const data = await res.json();
        setError(data.detail || 'Invalid or expired intake link');
      }
    } catch (err) {
      setError('Failed to load intake portal');
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Opposing Parties
  const addOpposingParty = () => {
    setFormData(prev => ({
      ...prev,
      opposing_parties: [...prev.opposing_parties, { name: '', relationship: 'opposing_party', notes: '' }]
    }));
  };

  const updateOpposingParty = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      opposing_parties: prev.opposing_parties.map((p, i) => 
        i === index ? { ...p, [field]: value } : p
      )
    }));
  };

  const removeOpposingParty = (index) => {
    setFormData(prev => ({
      ...prev,
      opposing_parties: prev.opposing_parties.filter((_, i) => i !== index)
    }));
  };

  // Other Related Parties
  const addRelatedParty = () => {
    setFormData(prev => ({
      ...prev,
      other_related_parties: [...prev.other_related_parties, { name: '', relationship: '', notes: '' }]
    }));
  };

  const updateRelatedParty = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      other_related_parties: prev.other_related_parties.map((p, i) => 
        i === index ? { ...p, [field]: value } : p
      )
    }));
  };

  const removeRelatedParty = (index) => {
    setFormData(prev => ({
      ...prev,
      other_related_parties: prev.other_related_parties.filter((_, i) => i !== index)
    }));
  };

  // Prior Attorneys
  const addPriorAttorney = () => {
    setFormData(prev => ({
      ...prev,
      prior_attorneys: [...prev.prior_attorneys, { name: '', firm_name: '', phone: '', email: '', representing: 'client_prior' }]
    }));
  };

  const updatePriorAttorney = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      prior_attorneys: prev.prior_attorneys.map((a, i) => 
        i === index ? { ...a, [field]: value } : a
      )
    }));
  };

  const removePriorAttorney = (index) => {
    setFormData(prev => ({
      ...prev,
      prior_attorneys: prev.prior_attorneys.filter((_, i) => i !== index)
    }));
  };

  // Opposing Attorneys
  const addOpposingAttorney = () => {
    setFormData(prev => ({
      ...prev,
      opposing_attorneys: [...prev.opposing_attorneys, { name: '', firm_name: '', phone: '', email: '', representing: 'opposing_party' }]
    }));
  };

  const updateOpposingAttorney = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      opposing_attorneys: prev.opposing_attorneys.map((a, i) => 
        i === index ? { ...a, [field]: value } : a
      )
    }));
  };

  const removeOpposingAttorney = (index) => {
    setFormData(prev => ({
      ...prev,
      opposing_attorneys: prev.opposing_attorneys.filter((_, i) => i !== index)
    }));
  };

  // Court Dates
  const addCourtDate = () => {
    setFormData(prev => ({
      ...prev,
      known_court_dates: [...prev.known_court_dates, { date: '', description: '', court_name: '' }]
    }));
  };

  const updateCourtDate = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      known_court_dates: prev.known_court_dates.map((c, i) => 
        i === index ? { ...c, [field]: value } : c
      )
    }));
  };

  const removeCourtDate = (index) => {
    setFormData(prev => ({
      ...prev,
      known_court_dates: prev.known_court_dates.filter((_, i) => i !== index)
    }));
  };

  // File handling
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const allowedTypes = intakeInfo?.allowed_types || [];
    
    const validFiles = files.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(ext)) {
        toast.error(`File type .${ext} is not allowed`);
        return false;
      }
      return true;
    });
    
    setSelectedFiles(prev => [...prev, ...validFiles].slice(0, intakeInfo?.max_files || 10));
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Form validation
  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.first_name && formData.last_name && formData.email && formData.phone;
      case 2:
        return formData.case_type && formData.case_description;
      case 3:
        return true; // Optional step
      case 4:
        return true; // Optional step
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Submit form
  const submitIntake = async () => {
    if (!validateStep(1) || !validateStep(2)) {
      toast.error('Please complete all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('intake_data', JSON.stringify(formData));
      
      // Add files
      selectedFiles.forEach(file => {
        submitData.append('files', file);
      });

      const res = await fetch(`${API_URL}/api/intake/client-intake/${token}`, {
        method: 'POST',
        body: submitData
      });

      if (res.ok) {
        const result = await res.json();
        setConfirmation(result);
        toast.success('Intake form submitted successfully!');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to submit intake form');
      }
    } catch (err) {
      toast.error('Failed to submit intake form');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-center">
          <Scale className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading intake form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold mb-2">Unable to Access Intake Form</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (confirmation) {
    return (
      <div className="min-h-screen bg-slate-50 py-8" data-testid="intake-confirmation-page">
        <div className="max-w-2xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-navy flex items-center justify-center">
              <Scale className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="font-heading text-xl font-bold text-navy">Legal AI</span>
              <span className="font-heading text-xl font-bold text-bronze">Dynamics</span>
            </div>
          </div>

          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="pt-8 text-center">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-emerald-800 mb-2">Intake Form Submitted!</h2>
              <p className="text-emerald-700 mb-6">{confirmation.message}</p>
              
              <div className="bg-white rounded-lg p-6 text-left mb-6">
                <h3 className="font-semibold mb-4 text-slate-800">Submission Summary</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Name:</span>
                    <span className="font-medium">{confirmation.contact_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Case Type:</span>
                    <span className="font-medium">{confirmation.case_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Documents Uploaded:</span>
                    <span className="font-medium">{confirmation.documents_uploaded} file(s)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Reference Number:</span>
                    <span className="font-mono font-medium">{confirmation.reference_number}</span>
                  </div>
                </div>
              </div>

              <div className="text-sm text-emerald-700">
                <p className="mb-2">
                  <strong>What happens next?</strong>
                </p>
                <p>
                  The law firm has been notified of your submission and will review your information. 
                  They will contact you soon using your preferred contact method.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="text-center mt-6 text-sm text-muted-foreground">
            <p>Please save your reference number: <strong className="font-mono">{confirmation.reference_number}</strong></p>
          </div>
        </div>
      </div>
    );
  }

  const steps = [
    { num: 1, title: 'Contact Info', icon: User },
    { num: 2, title: 'Case Details', icon: Briefcase },
    { num: 3, title: 'Related Parties', icon: Users },
    { num: 4, title: 'Attorneys & Dates', icon: Gavel },
    { num: 5, title: 'Documents', icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="client-intake-page">
      {/* Header */}
      <div className="max-w-3xl mx-auto px-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-navy flex items-center justify-center">
            <Scale className="w-6 h-6 text-white" />
          </div>
          <div>
            <span className="font-heading text-xl font-bold text-navy">Legal AI</span>
            <span className="font-heading text-xl font-bold text-bronze">Dynamics</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4">
        {/* Welcome Card */}
        <Card className="mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl">New Client Intake Form</CardTitle>
            <CardDescription>
              {intakeInfo.firm_name && (
                <span className="block">
                  Welcome! <strong>{intakeInfo.firm_name}</strong> has invited you to complete this intake form.
                </span>
              )}
              Please provide your information below. Fields marked with <span className="text-red-500">*</span> are required.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="w-4 h-4" />
              <span>Your information is securely transmitted and protected.</span>
            </div>
          </CardContent>
        </Card>

        {/* Progress Steps */}
        <div className="mb-6">
          <div className="flex justify-between">
            {steps.map((step) => (
              <div 
                key={step.num} 
                className={`flex flex-col items-center ${step.num <= currentStep ? 'text-emerald-600' : 'text-muted-foreground'}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                  step.num < currentStep ? 'bg-emerald-600 text-white' :
                  step.num === currentStep ? 'bg-emerald-100 text-emerald-600 border-2 border-emerald-600' :
                  'bg-slate-100'
                }`}>
                  {step.num < currentStep ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                <span className="text-xs font-medium hidden sm:block">{step.title}</span>
              </div>
            ))}
          </div>
          <Progress value={(currentStep / 5) * 100} className="mt-4 h-2" />
        </div>

        {/* Form Steps */}
        <Card>
          <CardContent className="pt-6">
            {/* Step 1: Contact Information */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <User className="w-5 h-5 text-emerald-600" />
                  Contact Information
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name <span className="text-red-500">*</span></Label>
                    <Input 
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => updateFormData('first_name', e.target.value)}
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name <span className="text-red-500">*</span></Label>
                    <Input 
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => updateFormData('last_name', e.target.value)}
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input 
                        id="email"
                        type="email"
                        className="pl-10"
                        value={formData.email}
                        onChange={(e) => updateFormData('email', e.target.value)}
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input 
                        id="phone"
                        type="tel"
                        className="pl-10"
                        value={formData.phone}
                        onChange={(e) => updateFormData('phone', e.target.value)}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="address">Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Textarea 
                      id="address"
                      className="pl-10"
                      value={formData.address}
                      onChange={(e) => updateFormData('address', e.target.value)}
                      placeholder="123 Main St, City, State ZIP"
                      rows={2}
                    />
                  </div>
                </div>

                <div>
                  <Label>Preferred Contact Method <span className="text-red-500">*</span></Label>
                  <div className="flex gap-4 mt-2">
                    {['phone', 'email', 'text'].map((method) => (
                      <label key={method} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={formData.preferred_contact_method === method}
                          onCheckedChange={() => updateFormData('preferred_contact_method', method)}
                        />
                        <span className="capitalize">{method}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Case Details */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-emerald-600" />
                  Case Details
                </h3>

                <div>
                  <Label htmlFor="case_type">Type of Legal Matter <span className="text-red-500">*</span></Label>
                  <Select value={formData.case_type} onValueChange={(v) => updateFormData('case_type', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select case type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {intakeInfo.case_types.map((type) => (
                        <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="case_description">
                    Please describe your situation <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="case_description"
                    value={formData.case_description}
                    onChange={(e) => updateFormData('case_description', e.target.value)}
                    placeholder="Please provide details about your legal matter, including what happened, when it occurred, and any other relevant information..."
                    rows={5}
                  />
                </div>

                <div>
                  <Label htmlFor="lead_source">How did you hear about us?</Label>
                  <Select value={formData.lead_source} onValueChange={(v) => updateFormData('lead_source', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {intakeInfo.lead_sources.map((source) => (
                        <SelectItem key={source.id} value={source.id}>{source.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 3: Related Parties */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-600" />
                  Related Parties
                </h3>

                {/* Opposing Parties */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Opposing Party(ies)</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addOpposingParty}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  {formData.opposing_parties.length === 0 && (
                    <p className="text-sm text-muted-foreground">No opposing parties added yet.</p>
                  )}
                  {formData.opposing_parties.map((party, index) => (
                    <div key={index} className="p-4 border rounded-lg bg-slate-50 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Opposing Party #{index + 1}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeOpposingParty(index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <Input 
                        placeholder="Name of opposing party"
                        value={party.name}
                        onChange={(e) => updateOpposingParty(index, 'name', e.target.value)}
                      />
                      <Input 
                        placeholder="Notes (relationship to you, role in case, etc.)"
                        value={party.notes}
                        onChange={(e) => updateOpposingParty(index, 'notes', e.target.value)}
                      />
                    </div>
                  ))}
                </div>

                {/* Other Related Parties */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Other Related Parties</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addRelatedParty}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Include children, important family members, guardian ad litem, or other relevant individuals.
                  </p>
                  {formData.other_related_parties.map((party, index) => (
                    <div key={index} className="p-4 border rounded-lg bg-slate-50 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Related Party #{index + 1}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeRelatedParty(index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <Input 
                        placeholder="Name"
                        value={party.name}
                        onChange={(e) => updateRelatedParty(index, 'name', e.target.value)}
                      />
                      <Select 
                        value={party.relationship} 
                        onValueChange={(v) => updateRelatedParty(index, 'relationship', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Relationship" />
                        </SelectTrigger>
                        <SelectContent>
                          {RELATIONSHIP_TYPES.map((rel) => (
                            <SelectItem key={rel.id} value={rel.id}>{rel.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input 
                        placeholder="Notes"
                        value={party.notes}
                        onChange={(e) => updateRelatedParty(index, 'notes', e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Attorneys & Court Dates */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Gavel className="w-5 h-5 text-emerald-600" />
                  Attorneys & Court Dates
                </h3>

                {/* Prior Attorneys */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Prior Attorney(s) Who Represented You</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addPriorAttorney}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  {formData.prior_attorneys.length === 0 && (
                    <p className="text-sm text-muted-foreground">No prior attorneys listed.</p>
                  )}
                  {formData.prior_attorneys.map((attorney, index) => (
                    <div key={index} className="p-4 border rounded-lg bg-slate-50 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Prior Attorney #{index + 1}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removePriorAttorney(index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Input 
                          placeholder="Attorney Name"
                          value={attorney.name}
                          onChange={(e) => updatePriorAttorney(index, 'name', e.target.value)}
                        />
                        <Input 
                          placeholder="Law Firm"
                          value={attorney.firm_name}
                          onChange={(e) => updatePriorAttorney(index, 'firm_name', e.target.value)}
                        />
                        <Input 
                          placeholder="Phone"
                          value={attorney.phone}
                          onChange={(e) => updatePriorAttorney(index, 'phone', e.target.value)}
                        />
                        <Input 
                          placeholder="Email"
                          value={attorney.email}
                          onChange={(e) => updatePriorAttorney(index, 'email', e.target.value)}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Opposing Party Attorneys */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Opposing Party's Attorney(s)</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addOpposingAttorney}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  {formData.opposing_attorneys.length === 0 && (
                    <p className="text-sm text-muted-foreground">No opposing attorneys listed.</p>
                  )}
                  {formData.opposing_attorneys.map((attorney, index) => (
                    <div key={index} className="p-4 border rounded-lg bg-amber-50 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Opposing Attorney #{index + 1}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeOpposingAttorney(index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Input 
                          placeholder="Attorney Name"
                          value={attorney.name}
                          onChange={(e) => updateOpposingAttorney(index, 'name', e.target.value)}
                        />
                        <Input 
                          placeholder="Law Firm"
                          value={attorney.firm_name}
                          onChange={(e) => updateOpposingAttorney(index, 'firm_name', e.target.value)}
                        />
                        <Input 
                          placeholder="Phone"
                          value={attorney.phone}
                          onChange={(e) => updateOpposingAttorney(index, 'phone', e.target.value)}
                        />
                        <Input 
                          placeholder="Email"
                          value={attorney.email}
                          onChange={(e) => updateOpposingAttorney(index, 'email', e.target.value)}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Known Court Dates */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Known Upcoming Court Dates</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addCourtDate}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  {formData.known_court_dates.length === 0 && (
                    <p className="text-sm text-muted-foreground">No upcoming court dates known.</p>
                  )}
                  {formData.known_court_dates.map((courtDate, index) => (
                    <div key={index} className="p-4 border rounded-lg bg-red-50 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Court Date #{index + 1}
                        </span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeCourtDate(index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Input 
                          type="date"
                          value={courtDate.date}
                          onChange={(e) => updateCourtDate(index, 'date', e.target.value)}
                        />
                        <Input 
                          placeholder="Court Name"
                          value={courtDate.court_name}
                          onChange={(e) => updateCourtDate(index, 'court_name', e.target.value)}
                        />
                      </div>
                      <Input 
                        placeholder="Description (e.g., Initial Hearing, Mediation, Trial)"
                        value={courtDate.description}
                        onChange={(e) => updateCourtDate(index, 'description', e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 5: Documents & Review */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  Documents & Additional Notes
                </h3>

                {/* Document Upload */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Upload Documents (Optional)</Label>
                  <p className="text-sm text-muted-foreground">
                    Upload any relevant documents such as ID, photos, medical records, contracts, etc.
                    Max {intakeInfo.max_files} files. Allowed types: {intakeInfo.allowed_types.map(t => `.${t}`).join(', ')}
                  </p>
                  
                  <div 
                    className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-emerald-400 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleFileSelect}
                      accept={intakeInfo.allowed_types.map(t => `.${t}`).join(',')}
                    />
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                    <p className="font-medium">Click to select files</p>
                    <p className="text-sm text-muted-foreground">or drag and drop</p>
                  </div>

                  {selectedFiles.length > 0 && (
                    <div className="space-y-2">
                      {selectedFiles.map((file, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <File className="w-5 h-5 text-emerald-600" />
                            <div>
                              <p className="font-medium text-sm">{file.name}</p>
                              <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removeSelectedFile(i)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Additional Notes */}
                <div>
                  <Label htmlFor="additional_notes">Additional Notes or Information</Label>
                  <Textarea
                    id="additional_notes"
                    value={formData.additional_notes}
                    onChange={(e) => updateFormData('additional_notes', e.target.value)}
                    placeholder="Is there anything else you'd like us to know?"
                    rows={4}
                  />
                </div>

                {/* Summary */}
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <h4 className="font-semibold text-emerald-800 mb-3">Ready to Submit</h4>
                  <div className="text-sm text-emerald-700 space-y-1">
                    <p>• Contact: {formData.first_name} {formData.last_name}</p>
                    <p>• Case Type: {intakeInfo.case_types.find(t => t.id === formData.case_type)?.name || 'Not selected'}</p>
                    <p>• Opposing Parties: {formData.opposing_parties.length}</p>
                    <p>• Related Parties: {formData.other_related_parties.length}</p>
                    <p>• Court Dates: {formData.known_court_dates.length}</p>
                    <p>• Documents: {selectedFiles.length} file(s)</p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              {currentStep > 1 ? (
                <Button variant="outline" onClick={prevStep}>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
              ) : (
                <div />
              )}

              {currentStep < 5 ? (
                <Button onClick={nextStep} className="bg-emerald-600 hover:bg-emerald-700">
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button 
                  onClick={submitIntake} 
                  disabled={submitting}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Submit Intake Form
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-muted-foreground">
          <p>Your information is securely transmitted and protected.</p>
        </div>
      </div>
    </div>
  );
}
